import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { getApiUrl } from '../utils/config';
import { 
  LoginRequest, 
  LoginResponse, 
  User, 
  DashboardCard, 
  DashboardStats, 
  ExcelUploadList, 
  ExcelData, 
  TopeDescuentoResult, 
  TopeDescuentoCliente, 
  TopeDescuentoClienteRubro, 
  TopeDescuentoClienteProducto, 
  RubroResponse, 
  ProductoRubro, 
  ConfiguracionTopeDescuento, 
  TopeDescuentoClienteRequest, 
  TopeDescuentoClienteRubroRequest, 
  TopeDescuentoClienteProductoRequest, 
  TopesDescuentoStats,
  ConsultaJerarquiaTopeDescuento,
  JerarquiaTopeDescuento,
  ConsultaFlexibleRequest,
  ConsultaFlexibleResult,
  ConsultaResumenTopes
} from '../types';

class ApiService {
  private api: AxiosInstance | null = null;

  private async initializeApi() {
    if (this.api) {
      return this.api;
    }

    const apiUrl = await getApiUrl();
    
    this.api = axios.create({
      baseURL: apiUrl,
      headers: {
        'Content-Type': 'application/json',
      }
      //,timeout: 10000, // 10 segundos de timeout
    });

    // Interceptor para agregar el token de autenticación
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor para manejar errores de autenticación
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        
        // Mejorar el mensaje de error para problemas de conexión
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          error.message = 'Tiempo de espera agotado. Verifica que el servidor esté ejecutándose.';
        } else if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
          error.message = 'Error de conexión. Verifica que el servidor esté ejecutándose.';
        }
        
        return Promise.reject(error);
      }
    );

    return this.api;
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const api = await this.initializeApi();
    const response: AxiosResponse<LoginResponse> = await api.post('/auth/login', credentials);
    return response.data;
  }

  async refreshToken(refreshToken: string): Promise<LoginResponse> {
    const api = await this.initializeApi();
    const response: AxiosResponse<LoginResponse> = await api.post('/auth/refresh', { refreshToken });
    return response.data;
  }

  async logout(refreshToken: string): Promise<void> {
    const api = await this.initializeApi();
    await api.post('/auth/logout', { refreshToken });
  }

  async getCurrentUser(): Promise<User> {
    const api = await this.initializeApi();
    const response: AxiosResponse<User> = await api.get('/auth/me');
    return response.data;
  }

  // Dashboard endpoints
  async getDashboardCards(): Promise<DashboardCard[]> {
    const api = await this.initializeApi();
    const response: AxiosResponse<DashboardCard[]> = await api.get('/dashboard/cards');
    return response.data;
  }

  async getDashboardStats(): Promise<DashboardStats> {
    const api = await this.initializeApi();
    const response: AxiosResponse<DashboardStats> = await api.get('/dashboard/stats');
    return response.data;
  }

  // Excel endpoints
  async uploadFile(file: File): Promise<any> {
    const api = await this.initializeApi();
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/excel/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getUploads(pageNumber: number = 1, pageSize: number = 10): Promise<ExcelUploadList> {
    const api = await this.initializeApi();
    const response: AxiosResponse<ExcelUploadList> = await api.get('/excel/uploads', {
      params: { pageNumber, pageSize },
    });
    return response.data;
  }

  async getUploadById(uploadId: number): Promise<any> {
    const api = await this.initializeApi();
    const response = await api.get(`/excel/uploads/${uploadId}`);
    return response.data;
  }

  async processUpload(uploadId: number): Promise<any> {
    const api = await this.initializeApi();
    const response = await api.post(`/excel/uploads/${uploadId}/process`);
    return response.data;
  }

  async getUploadData(uploadId: number, pageNumber: number = 1, pageSize: number = 50): Promise<ExcelData[]> {
    const api = await this.initializeApi();
    const response: AxiosResponse<ExcelData[]> = await api.get(`/excel/uploads/${uploadId}/data`, {
      params: { pageNumber, pageSize },
    });
    return response.data;
  }

  async deleteUpload(uploadId: number): Promise<any> {
    const api = await this.initializeApi();
    const response = await api.delete(`/excel/uploads/${uploadId}`);
    return response.data;
  }

  // Configuración
  async getConfiguration(): Promise<any> {
    const api = await this.initializeApi();
    const response = await api.get('/configuracion');
    return response.data;
  }

  async saveConfiguration(config: any): Promise<any> {
    const api = await this.initializeApi();
    const response = await api.post('/configuracion', config);
    return response.data;
  }

  async testConnection(type: 'postgres' | 'sqlserver', connection: any): Promise<any> {
    const api = await this.initializeApi();
    const endpoint = type === 'postgres' ? '/configuracion/test-postgres' : '/configuracion/test-sqlserver';
    const response = await api.post(endpoint, { connection });
    return response.data;
  }

  // =====================================================
  // ENDPOINTS PARA TOPES DE DESCUENTO
  // =====================================================

  // Consultas de Topes de Descuento
  async consultarTopeDescuento(clienteId: string, productoId: string): Promise<TopeDescuentoResult> {
    const api = await this.initializeApi();
    const response: AxiosResponse<TopeDescuentoResult> = await api.get(`/topedescuento/consultar/${clienteId}/${productoId}`);
    return response.data;
  }

  async consultarTopeDescuentoSimple(clienteId: string, productoId: string): Promise<number> {
    const api = await this.initializeApi();
    const response: AxiosResponse<number> = await api.get(`/topedescuento/consultar-simple/${clienteId}/${productoId}`);
    return response.data;
  }

  async obtenerRubrosDisponibles(): Promise<RubroResponse[]> {
    const api = await this.initializeApi();
    const response: AxiosResponse<RubroResponse[]> = await api.get('/topedescuento/rubros');
    return response.data;
  }

  async obtenerProductosPorRubro(rubroCodigo: string): Promise<ProductoRubro[]> {
    const api = await this.initializeApi();
    const response: AxiosResponse<ProductoRubro[]> = await api.get(`/topedescuento/productos-por-rubro/${rubroCodigo}`);
    return response.data;
  }

  // Gestión de Topes por Cliente
  async obtenerTopesCliente(clienteId?: string): Promise<TopeDescuentoCliente[]> {
    const api = await this.initializeApi();
    const params = clienteId ? { clienteId } : {};
    const response: AxiosResponse<TopeDescuentoCliente[]> = await api.get('/topedescuento/cliente', { params });
    return response.data;
  }

  async obtenerTopeClientePorId(id: number): Promise<TopeDescuentoCliente> {
    const api = await this.initializeApi();
    const response: AxiosResponse<TopeDescuentoCliente> = await api.get(`/topedescuento/cliente/${id}`);
    return response.data;
  }

  async crearTopeCliente(request: TopeDescuentoClienteRequest): Promise<TopeDescuentoCliente> {
    const api = await this.initializeApi();
    const response: AxiosResponse<TopeDescuentoCliente> = await api.post('/topedescuento/cliente', request);
    return response.data;
  }

  async actualizarTopeCliente(id: number, request: TopeDescuentoClienteRequest): Promise<TopeDescuentoCliente> {
    const api = await this.initializeApi();
    const response: AxiosResponse<TopeDescuentoCliente> = await api.put(`/topedescuento/cliente/${id}`, request);
    return response.data;
  }

  async eliminarTopeCliente(id: number): Promise<void> {
    const api = await this.initializeApi();
    await api.delete(`/topedescuento/cliente/${id}`);
  }

  // Gestión de Topes por Cliente y Rubro
  async obtenerTopesClienteRubro(clienteId?: string, rubroCodigo?: string): Promise<TopeDescuentoClienteRubro[]> {
    const api = await this.initializeApi();
    const params: any = {};
    if (clienteId) params.clienteId = clienteId;
    if (rubroCodigo) params.rubroCodigo = rubroCodigo;
    
    const response: AxiosResponse<TopeDescuentoClienteRubro[]> = await api.get('/topedescuento/cliente-rubro', { params });
    return response.data;
  }

  async obtenerTopeClienteRubroPorId(id: number): Promise<TopeDescuentoClienteRubro> {
    const api = await this.initializeApi();
    const response: AxiosResponse<TopeDescuentoClienteRubro> = await api.get(`/topedescuento/cliente-rubro/${id}`);
    return response.data;
  }

  async crearTopeClienteRubro(request: TopeDescuentoClienteRubroRequest): Promise<TopeDescuentoClienteRubro> {
    const api = await this.initializeApi();
    const response: AxiosResponse<TopeDescuentoClienteRubro> = await api.post('/topedescuento/cliente-rubro', request);
    return response.data;
  }

  async actualizarTopeClienteRubro(id: number, request: TopeDescuentoClienteRubroRequest): Promise<TopeDescuentoClienteRubro> {
    const api = await this.initializeApi();
    const response: AxiosResponse<TopeDescuentoClienteRubro> = await api.put(`/topedescuento/cliente-rubro/${id}`, request);
    return response.data;
  }

  async eliminarTopeClienteRubro(id: number): Promise<void> {
    const api = await this.initializeApi();
    await api.delete(`/topedescuento/cliente-rubro/${id}`);
  }

  // Gestión de Topes por Cliente y Producto
  async obtenerTopesClienteProducto(clienteId?: string, productoId?: string): Promise<TopeDescuentoClienteProducto[]> {
    const api = await this.initializeApi();
    const params: any = {};
    if (clienteId) params.clienteId = clienteId;
    if (productoId) params.productoId = productoId;
    
    const response: AxiosResponse<TopeDescuentoClienteProducto[]> = await api.get('/topedescuento/cliente-producto', { params });
    return response.data;
  }

  async obtenerTopeClienteProductoPorId(id: number): Promise<TopeDescuentoClienteProducto> {
    const api = await this.initializeApi();
    const response: AxiosResponse<TopeDescuentoClienteProducto> = await api.get(`/topedescuento/cliente-producto/${id}`);
    return response.data;
  }

  async crearTopeClienteProducto(request: TopeDescuentoClienteProductoRequest): Promise<TopeDescuentoClienteProducto> {
    const api = await this.initializeApi();
    const response: AxiosResponse<TopeDescuentoClienteProducto> = await api.post('/topedescuento/cliente-producto', request);
    return response.data;
  }

  async actualizarTopeClienteProducto(id: number, request: TopeDescuentoClienteProductoRequest): Promise<TopeDescuentoClienteProducto> {
    const api = await this.initializeApi();
    const response: AxiosResponse<TopeDescuentoClienteProducto> = await api.put(`/topedescuento/cliente-producto/${id}`, request);
    return response.data;
  }

  async eliminarTopeClienteProducto(id: number): Promise<void> {
    const api = await this.initializeApi();
    await api.delete(`/topedescuento/cliente-producto/${id}`);
  }

  // Configuración de Topes de Descuento
  async obtenerConfiguracionTopeDescuento(): Promise<ConfiguracionTopeDescuento[]> {
    const api = await this.initializeApi();
    const response: AxiosResponse<ConfiguracionTopeDescuento[]> = await api.get('/topedescuento/configuracion');
    return response.data;
  }

  async actualizarConfiguracionTopeDescuento(clave: string, valor: string): Promise<ConfiguracionTopeDescuento> {
    const api = await this.initializeApi();
    const response: AxiosResponse<ConfiguracionTopeDescuento> = await api.put(`/topedescuento/configuracion/${clave}`, { valor });
    return response.data;
  }

  // Utilidades
  async verifyTopesSchema() {
    const api = await this.initializeApi();
    const response = await api.post('/topedescuento/verify-schema');
    return response.data;
  }

  async createTopesSchema() {
    const api = await this.initializeApi();
    const response = await api.post('/topedescuento/create-schema');
    return response.data;
  }

  async obtenerProductos(search?: string): Promise<ProductoRubro[]> {
    const api = await this.initializeApi();
    const params = search ? { search } : {};
    const response: AxiosResponse<ProductoRubro[]> = await api.get('/topedescuento/productos', { params });
    return response.data;
  }

  async resetUpload(uploadId: number): Promise<{ success: boolean }> {
    const api = await this.initializeApi();
    const response = await api.post(`/excel/uploads/${uploadId}/reset`);
    return response.data;
  }

  async consultarJerarquiaCompleta(clienteId: string, productoId: string): Promise<ConsultaJerarquiaTopeDescuento> {
    const api = await this.initializeApi();
    const response: AxiosResponse<ConsultaJerarquiaTopeDescuento> = await api.get(`/topedescuento/consultar-jerarquia/${clienteId}/${productoId}`);
    return response.data;
  }

  async consultarTopesFlexible(request: ConsultaFlexibleRequest): Promise<ConsultaFlexibleResult> {
    const api = await this.initializeApi();
    const response: AxiosResponse<ConsultaFlexibleResult> = await api.post('/topedescuento/consultar-flexible', request);
    return response.data;
  }

  async obtenerResumenTopes(): Promise<ConsultaResumenTopes> {
    const api = await this.initializeApi();
    const response: AxiosResponse<ConsultaResumenTopes> = await api.get('/topedescuento/resumen');
    return response.data;
  }

  async obtenerClientesDisponibles(search?: string): Promise<string[]> {
    const api = await this.initializeApi();
    const params = search ? { search } : {};
    const response: AxiosResponse<string[]> = await api.get('/topedescuento/clientes', { params });
    return response.data;
  }

  async obtenerProductosDisponibles(search?: string): Promise<string[]> {
    const api = await this.initializeApi();
    const params = search ? { search } : {};
    const response: AxiosResponse<string[]> = await api.get('/topedescuento/productos', { params });
    return response.data;
  }

  async exportarTopesExcel(request: ConsultaFlexibleRequest): Promise<Blob> {
    const api = await this.initializeApi();
    
    const response = await api.post('/TopeDescuento/exportar-excel', request, {
      responseType: 'blob',
    });
    return response.data;
  }
}

export const apiService = new ApiService();